import {FormControl, FormField} from 'platform/components';

import {memo, useMemo} from 'react';

import {VehicleTypeEnumObject} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useVehicleMake} from '@dms/shared';

import {Nullish, RequiredTestIdProps} from 'shared/web';

import {BaseVehicle} from '../../core/types/BaseVehicle';
import {useStepFieldRegistration} from '../../kit/hooks/useStepFieldRegistration';

interface VehicleMakeSelectProps extends RequiredTestIdProps {
  vehicleType: VehicleTypeEnumObject | null;
  onChange?: (value: string | Nullish) => void;
  onCreateOption: (value: string) => void;
  control?: FormControl<BaseVehicle>;
  unknownMakes?: Array<{value: string; label: string}>;
  isLoading?: boolean;
  isRequired?: boolean;
  isRecommended?: boolean;
  label?: string;
}

/**
 * Vehicle Make Select component
 *
 * @example
 * ```tsx
 * <VehicleForm.Section title="Basic Info">
 *   <VehicleForm.Group>
 *     <VehicleMakeSelect data-testid="make-select" />
 *     <VehicleModelSelect data-testid="model-select" />
 *   </VehicleForm.Group>
 * </VehicleForm.Section>
 * ```
 */
export const VehicleMakeSelect = memo<VehicleMakeSelectProps>((props: VehicleMakeSelectProps) => {
  const [{groupedMakeOptionsWithTopMakes: catalogMakeOptions}, {isLoading: isLoadingMakes}] =
    useVehicleMake(props.vehicleType);

  useStepFieldRegistration('formMake');

  // Merge catalog options with unknown makes
  const makeOptions = useMemo(() => {
    const catalogOptions = catalogMakeOptions ?? [];
    const unknownOptions = props.unknownMakes ?? [];

    if (unknownOptions.length === 0) {
      return catalogOptions;
    }

    const newUnknownOptions = unknownOptions.filter(
      (unknownMake) =>
        !catalogOptions.some((catalogGroup) =>
          catalogGroup.options.some((option) => option.value === unknownMake.value)
        )
    );

    if (newUnknownOptions.length === 0) {
      return catalogOptions;
    }

    if (catalogOptions.length > 0) {
      const lastGroup = catalogOptions.at(-1);
      const updatedLastGroup = {
        ...lastGroup,
        options: [...(lastGroup?.options ?? []), ...newUnknownOptions],
      };
      return [...catalogOptions.slice(0, -1), updatedLastGroup];
    }

    // If no catalog options, create a group for unknown makes
    return [
      {
        label: i18n.t('general.labels.allMakes'),
        options: newUnknownOptions,
      },
    ];
  }, [catalogMakeOptions, props.unknownMakes]);

  const isLoading = isLoadingMakes || props.isLoading;

  if (!props.control) {
    throw new Error(
      'VehicleMakeSelect requires control prop. Use within VehicleForm.FieldWrapper or pass control directly.'
    );
  }

  return (
    <FormField
      type="creatableChoice"
      control={props.control}
      name="formMake"
      label={props.label ?? i18n.t('entity.vehicle.labels.make')}
      isRecommended={props.isRecommended}
      isLoading={isLoading}
      options={makeOptions ?? []}
      onChange={
        props.onChange ? (newValue) => props.onChange?.(newValue as string | Nullish) : undefined
      }
      onCreateOption={props.onCreateOption}
      isRequired={props.isRequired}
      data-testid={props['data-testid']}
    />
  );
});

VehicleMakeSelect.displayName = 'VehicleMakeSelect';
